<template>
  <div>
    <institution-diploma-proceed-modal @confirm="rejectDiploma"
                                       :reason-required="true"
                                       :reason-label="t('REJECTION_REASON')"
                                       :confirm-button-label="t('REJECT')"
                                       ref="diplomaRejectModalRef"/>

    <institution-diploma-proceed-modal @confirm="approveDiploma"
                                       :reason-required="false"
                                       :reason-label="t('APPROVAL_REASON')"
                                       ref="diplomaApprovalModalRef"/>

    <institution-diploma-modal @confirm="afterDiplomaSave" ref="diplomaModalRef"/>

    <div class="d-flex justify-content-end" v-if="props.accountId && auth.hasSomeRole(Role.ADMIN, Role.VIEWER)">
      <Button :label="t('ADD_NEW_DOCUMENT')" @click="showModalForNewDiplomaDocument(props.accountId)"/>
    </div>

    <PaginatedTable id="paginated-institutionDiplomas"
                    :rows-per-page="props.rowsPerPage"
                    :fixed-rows-per-page="props.fixedRowsPerPage"
                    :on-page-change="getInstitutionDiplomas"
                    :fields="diplomaFields"
                    :values-to-save="props.searchParams"
                    :small="!props.isCompact"
                    ref="diplomasTableRef"
    >
      <template v-slot:cell(fileName)="{ item }">
        <div v-if="item['fileName']">
          <span v-if="auth.hasSomeRole(Role.ADMIN, Role.VIEWER, Role.TRAINEE)" v-tooltip="t('DOWNLOAD')">
            <a href="#" @click.prevent="downloadDocument(item['fileName'], item['id'])">{{ item['fileName'] }}</a>
          </span>
          <span v-else>
            {{ item['fileName'] }}
          </span>
        </div>
      </template>

      <template v-slot:cell(accountSsn)="{ item }">
        <router-link :to="{ name: 'Trainee', params: { id: `${item['accountId']}` } }">{{ item['accountSsn'] }}</router-link>
      </template>

      <template v-slot:cell(documentType)="{ item }">
        {{ t(`DOCUMENT_TYPES.${item['documentType']}`) }}
      </template>

      <template v-slot:cell(status)="{ item }">
        <span v-if="DiplomaStatus.REJECTED === item['status']" class="text-danger fw-bold">
          {{ t(`DIPLOMA_STATUSES.${item['status']}`) }}
        </span>
        <span v-else-if="DiplomaStatus.APPROVED === item['status']" class="text-success fw-bold">
          {{ t(`DIPLOMA_STATUSES.${item['status']}`) }}
        </span>
        <span v-else>
          {{ t(`DIPLOMA_STATUSES.${item['status']}`) }}
        </span>

        <span class="ms-2" v-if="DiplomaStatus.REJECTED === item['status']" v-tooltip="item['rejectionReason']">
          <i class="pi pi-info-circle text-danger"/>
        </span>

        <span class="ms-2" v-if="DiplomaStatus.APPROVED === item['status'] && item['approvalReason']" v-tooltip="item['approvalReason']">
          <i class="pi pi-info-circle text-success"/>
        </span>
      </template>

      <template v-slot:cell(actions)="{ item }">
        <div class="text-nowrap row-actions">
          <Button v-if="auth.hasRole(Role.ADMIN)"
                  v-tooltip="t('REMOVE')"
                  icon="pi pi-trash"
                  severity="danger"
                  text
                  rounded
                  @click="showDeleteDocumentConfirmation(item['id'])"
          />

          <template v-if="auth.hasSomeRole(Role.ADMIN, Role.VIEWER)">
            <Button v-if="[DiplomaStatus.PENDING, DiplomaStatus.REJECTED].includes(item['status'])"
                    v-tooltip="t('EDIT')"
                    icon="pi pi-pencil"
                    text
                    rounded
                    @click="showModalForUpdateDiplomaDocument(item['id'], item)"
            />
            <Button v-else
                    v-tooltip="t('VIEW')"
                    icon="pi pi-info-circle"
                    text
                    rounded
                    @click="showModalForUpdateDiplomaDocument(item['id'], item)"
            />
          </template>

          <template v-if="DiplomaStatus.PENDING === item['status'] && auth.hasSomeRole(Role.ADMIN) && auth.getSsn() !== item['createdBy']">
            <Button v-tooltip="t('APPROVE')"
                    icon="pi pi-check-circle"
                    severity="success"
                    text
                    rounded
                    @click="showApproveDiplomaConfirmation(item['id'])"
            />
            <Button v-tooltip="t('REJECT')"
                    icon="pi pi-times-circle"
                    severity="danger"
                    text
                    rounded
                    @click="showRejectDiplomaConfirmation(item['id'])"
            />
          </template>
        </div>
      </template>
    </PaginatedTable>

    <file-downloader ref="fileDownloaderRef"/>
  </div>
</template>

<script setup lang="ts">
import {computed, ref} from "vue";
import {injectAuth, injectAxios} from "@/util/inject";
import {Role} from "@/auth/oauth";
import {useI18n} from "vue-i18n";
import {useToast} from "@/util/toastUtils";
import {DiplomaStatus, type InstitutionDiplomaDto} from "@/model/institution-diploma";
import PaginatedTable, {type PaginationRequest} from "@/views/components/PaginatedTable.vue";
import eventBus from "@/util/eventBus";
import InstitutionDiplomaProceedModal from "@/views/components/modals/InstitutionDiplomaProceedModal.vue";
import InstitutionDiplomaModal from "@/views/components/modals/InstitutionDiplomaModal.vue";
import FileDownloader from "@/components/FileDownloader.vue";

const auth = injectAuth();
const axios = injectAxios();
const {t} = useI18n();
const toast = useToast();

const props = defineProps<{
  accountId?: string;
  isCompact?: boolean;
  searchParams?: {
    searchValue?: string;
    status?: string | null
  };
  rowsPerPage?: number;
  fixedRowsPerPage?: number;
}>();

const emit = defineEmits<{
  onReset: []
}>();

const diplomaFields = computed(() => {
  const fields = [
    {key: "accountName", label: t("NAME"), sortable: true, show: !props.accountId},
    {key: "accountSsn", label: t("SSN"), sortable: true, show: !props.accountId},
    {key: "diplomaLevel", label: t("LANGUAGE_DIPLOMA_LEVEL"), sortable: true, show: true},
    {key: "institutionName", label: t("INSTITUTION"), sortable: true, show: !props.isCompact},
    {key: "fileName", label: t("FILE"), sortable: true, show: true},
    {key: "documentType", label: t("DOCUMENT_TYPE"), sortable: true, show: !props.isCompact},
    {key: "documentNumber", label: t("DOCUMENT_NUMBER"), sortable: true, show: !props.isCompact},
    {key: "numberOfLessons", label: t("NUMBER_OF_CONTACT_HOURS"), sortable: true, show: !props.isCompact},
    {key: "senderEmail", label: t("SENDER_EMAIL"), sortable: true, show: auth.hasSomeRole(Role.ADMIN, Role.VIEWER)},
    {key: "status", label: t("STATUS"), sortable: true, show: true},
    {key: "actions", label: "", sortable: false, show: auth.hasSomeRole(Role.ADMIN, Role.VIEWER)},
  ];

  return fields.filter(f => f.show);
});

const diplomasTableRef = ref<InstanceType<typeof PaginatedTable> | null>(null);
const diplomaModalRef = ref<InstanceType<typeof InstitutionDiplomaModal> | null>(null);
const diplomaApprovalModalRef = ref<InstanceType<typeof InstitutionDiplomaProceedModal> | null>(null);
const diplomaRejectModalRef = ref<InstanceType<typeof InstitutionDiplomaProceedModal> | null>(null);
const fileDownloaderRef = ref<InstanceType<typeof FileDownloader> | null>(null);

const afterDiplomaSave = async () => {
  await resetTableInternal();
  toast.makeSuccessToast(t("OPERATION_SUCCESSFUL"));
};

const getInstitutionDiplomas = async (data: PaginationRequest) => {
  let params = {
    ...data,
  };

  if (props.searchParams) {
    params = {
      ...params,
      ...props.searchParams,
    };
  }

  if (props.accountId) {
    return await axios.get(`/account/${props.accountId}/institutions/diplomas`, {params: params});
  }

  return await axios.get(`/institutions/diplomas`, {params: params});
};

const deleteInstitutionDiploma = async (id: string) => {
  await axios.delete(`/institutions/diplomas/${id}`);
  toast.makeSuccessToast(t("REMOVED"));
  await resetTableInternal();
};

const showDeleteDocumentConfirmation = (id: string) => {
  eventBus.$emit("show-confirmation-modal", {
    callback: () => deleteInstitutionDiploma(id),
  });
};

const showApproveDiplomaConfirmation = (id: string) => {
  diplomaApprovalModalRef.value?.showConfirmationModal(id);
};

const showRejectDiplomaConfirmation = (id: string) => {
  diplomaRejectModalRef.value?.showConfirmationModal(id);
};

const showModalForNewDiplomaDocument = (accountId: string) => {
  diplomaModalRef.value?.openForNewDiploma(accountId);
};

const showModalForUpdateDiplomaDocument = (accountId: string, data: InstitutionDiplomaDto) => {
  diplomaModalRef.value?.openToUpdateDiploma(accountId, data);
};

const downloadDocument = async (fileName: string, id: string) => {
  await fileDownloaderRef.value?.downloadFile(`/institutions/diplomas/${id}/download`, {}, fileName);
};

const approveDiploma = async (id: string, reason: string) => {
  await axios.patch(`/institutions/diplomas/${id}/approve`, {approvalReason: reason});
  toast.makeSuccessToast(t("OPERATION_SUCCESSFUL"));
  await resetTableInternal();
};

const rejectDiploma = async (id: string, reason: string) => {
  await axios.patch(`/institutions/diplomas/${id}/reject`, {rejectionReason: reason});
  toast.makeSuccessToast(t("OPERATION_SUCCESSFUL"));
  await resetTableInternal();
};

const resetTableInternal = async () => {
  await emit("onReset");
  diplomasTableRef.value?.reset();
};

const resetTable = () => {
  diplomasTableRef.value?.reset();
};

const numberOfResults = () => {
  return diplomasTableRef.value ? diplomasTableRef.value.numberOfResults() : 0;
};

defineExpose({
  resetTable,
  numberOfResults,
});
</script>

<style scoped lang="scss">
.row-actions {
  & > button:not(:first-child) {
    margin-left: 0.25rem;
  }
}
</style>

<style lang="scss">
.tooltip-inner {
  text-align: left;
  white-space: break-spaces;
}
</style>
